/**
 * Σύρσιμο ΑΡΙΣΤΕΡΑ σε γραμμή = γρήγορη διαγραφή (σύνοψη του PDA — ιδιοκτήτης 8/10/2026).
 *
 * Μόνο αριστερά: το σύρσιμο δεξιά στη σύνοψη είναι το «πίσω στα προϊόντα» της σελίδας. Με
 * touch-action «pan-y pan-left» ο browser κρατά την κάθετη κύλιση και το σύρσιμο δεξιά (native,
 * όπως πριν) και δίνει εδώ μόνο το αριστερά. Κάτω από το όριο η γραμμή γυρίζει πίσω· πάνω από αυτό
 * (το κόκκινο γίνεται «Άφησε για διαγραφή», μικρή δόνηση) φεύγει — ο γονέας δίνει «Αναίρεση».
 * Ένα σύρσιμο δεν «πατά» τίποτα από κάτω (ούτε άνοιγμα γραμμής ούτε πιάτο).
 */

import React, { useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';

/** Πόσο πρέπει να συρθεί (κλάσμα του πλάτους) για να σβήσει. */
export const SWIPE_DELETE_FRACTION = 0.4;
/** Από πόσα px κίνησης παύει να είναι πάτημα. */
const START_SLOP_PX = 10;
/** Πλάτος όταν δεν μετριέται (jsdom). */
const FALLBACK_WIDTH = 360;

const SwipeToDelete: React.FC<{
  onDelete: () => void;
  /** Κλάσεις της κινούμενης επιφάνειας (π.χ. φόντο — πρέπει να είναι αδιαφανές). */
  className?: string;
  children: React.ReactNode;
}> = ({ onDelete, className = '', children }) => {
  const start = useRef<{ id: number; x: number; y: number } | null>(null);
  const dragging = useRef(false);
  const armedRef = useRef(false);
  const swallowClick = useRef(false);
  const width = useRef(FALLBACK_WIDTH);
  const [offset, setOffset] = useState(0);
  const [moving, setMoving] = useState(false);
  const [armed, setArmedState] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const setArmed = (value: boolean) => {
    armedRef.current = value;
    setArmedState(value);
  };

  const reset = () => {
    start.current = null;
    dragging.current = false;
    setMoving(false);
    setArmed(false);
    setOffset(0);
  };

  return (
    <div className="relative overflow-hidden">
      <div
        aria-hidden="true"
        className={`absolute inset-0 flex items-center justify-end gap-2 pr-4 text-white font-bold text-sm ${
          armed || leaving ? 'bg-red-600' : 'bg-red-500'
        }`}
      >
        <Trash2 className={`w-5 h-5 transition-transform ${armed || leaving ? 'scale-125' : ''}`} />
        <span>{armed || leaving ? 'Άφησε για διαγραφή' : 'Διαγραφή'}</span>
      </div>
      <div
        data-testid="swipe-row"
        className={`relative ${className}`}
        style={{
          touchAction: 'pan-y pan-left',
          transform: offset ? `translateX(${offset}px)` : undefined,
          transition: moving ? 'none' : 'transform 160ms ease-out',
        }}
        onPointerDown={(e) => {
          // Νέα κίνηση: ό,τι «κλικ» περίμενε το προηγούμενο σύρσιμο δεν ήρθε — να μη φάει αυτό το πάτημα.
          swallowClick.current = false;
          if (leaving || !e.isPrimary || e.pointerType === 'mouse') return;
          start.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
          dragging.current = false;
          width.current = e.currentTarget.offsetWidth || FALLBACK_WIDTH;
        }}
        onPointerMove={(e) => {
          const s = start.current;
          if (!s || e.pointerId !== s.id) return;
          const dx = e.clientX - s.x;
          const dy = e.clientY - s.y;
          if (!dragging.current) {
            if (Math.abs(dx) < START_SLOP_PX && Math.abs(dy) < START_SLOP_PX) return;
            // Κάθετα ή δεξιά: δουλειά του browser (κύλιση / «πίσω» της σελίδας) — όχι δική μας.
            if (dx >= 0 || Math.abs(dy) > Math.abs(dx)) {
              start.current = null;
              return;
            }
            dragging.current = true;
            setMoving(true);
            e.currentTarget.setPointerCapture?.(e.pointerId);
          }
          const next = Math.max(-width.current, Math.min(0, dx));
          setOffset(next);
          const nowArmed = -next >= width.current * SWIPE_DELETE_FRACTION;
          if (nowArmed !== armedRef.current) {
            if (nowArmed) navigator.vibrate?.(10);
            setArmed(nowArmed);
          }
        }}
        onPointerUp={(e) => {
          if (!start.current || e.pointerId !== start.current.id) return;
          const wasDragging = dragging.current;
          start.current = null;
          dragging.current = false;
          setMoving(false);
          if (!wasDragging) return;
          swallowClick.current = true;
          if (armedRef.current) {
            setLeaving(true);
            setOffset(-width.current);
            window.setTimeout(onDelete, 170);
          } else {
            setArmed(false);
            setOffset(0);
          }
        }}
        onPointerCancel={() => {
          if (!leaving) reset();
        }}
        onClickCapture={(e) => {
          // Το σύρσιμο τελειώνει με «κλικ» στο στοιχείο από κάτω — να μην ανοίξει γραμμή ή αλλάξει πιάτο.
          if (swallowClick.current) {
            swallowClick.current = false;
            e.stopPropagation();
            e.preventDefault();
          }
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeToDelete;
